import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import apiClient, { getErrorMessage } from '../../api/client';
import { useToast } from '../../hooks/useToast';
import { FiArrowLeft, FiFileText, FiAlertTriangle, FiCheck, FiPrinter, FiShare2 } from 'react-icons/fi';

const printStyles = `
  @media print {
    @page { size: A4; margin: 1.2cm; }
    body * { visibility: hidden; }
    .payslip-print-area, .payslip-print-area * { visibility: visible; }
    .payslip-print-area {
      position: absolute; left: 0; top: 0; width: 100%; padding: 0;
      font-size: 11px;
    }
    .no-print { display: none !important; }
    .payslip-print-area .stats-row {
      grid-template-columns: repeat(4, 1fr);
      gap: 0.5rem;
      margin-bottom: 0.75rem;
      page-break-inside: avoid;
    }
    .payslip-print-area .stat-box {
      padding: 0.5rem;
      box-shadow: none;
      border: 1px solid #d1d5db;
    }
    .payslip-print-area .stat-box .value {
      font-size: 1.1rem;
    }
    .payslip-print-area .stat-box .label {
      font-size: 0.7rem;
      margin-bottom: 0.2rem;
    }
    /* This is the fix: overflow-x:auto on the shared .table-container class makes
       Chrome's print engine collapse each row into stacked blocks instead of columns.
       Force it back to visible for print, and pin the table's own layout explicitly. */
    .payslip-print-area .table-container {
      overflow: visible !important;
      page-break-inside: avoid;
      border: 1px solid #d1d5db;
    }
    .payslip-print-area table.data-table {
      display: table !important;
      width: 100% !important;
      table-layout: fixed !important;
      border-collapse: collapse !important;
    }
    .payslip-print-area table.data-table thead {
      display: table-header-group !important;
    }
    .payslip-print-area table.data-table tbody {
      display: table-row-group !important;
    }
    .payslip-print-area table.data-table tr {
      display: table-row !important;
    }
    .payslip-print-area table.data-table th,
    .payslip-print-area table.data-table td {
      display: table-cell !important;
      padding: 0.4rem 0.6rem;
      font-size: 11px;
    }
    .payslip-print-area h2 {
      font-size: 1.1rem;
      margin-bottom: 0.2rem;
    }
    .payslip-print-area p {
      font-size: 0.8rem;
    }
  }
`;

const Payslips = () => {
  const { employeeId } = useParams();
  const { success, error: showError } = useToast();
  const [employee, setEmployee] = useState(null);
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [components, setComponents] = useState(null);
  const [componentTypeNames, setComponentTypeNames] = useState({});
  const [taxRegime, setTaxRegime] = useState(null);
  const [marking, setMarking] = useState(false);

  useEffect(() => {
    apiClient.get(`/hr/employees/${employeeId}`)
      .then(({ data }) => setEmployee(data.data))
      .catch(() => { /* name just won't show in share text — non-critical */ });

    apiClient.get(`/payroll/payslips/employees/${employeeId}`)
      .then(({ data }) => setPayslips(data.data))
      .catch((err) => showError(getErrorMessage(err, 'Could not load payslips')))
      .finally(() => setLoading(false));

    apiClient.get('/payroll/payslips/component-types')
      .then(({ data }) => {
        const map = {};
        data.data.forEach((t) => { map[t.id] = t.name; });
        setComponentTypeNames(map);
      })
      .catch(() => { /* falls back to "Type #N" if this fails — non-critical */ });

    apiClient.get('/payroll/runs/tax-regime')
      .then(({ data }) => setTaxRegime(data.data))
      .catch(() => { /* banner just won't show — non-critical */ });
  }, [employeeId, showError]);

  const viewDetail = async (payslip) => {
    setSelected(payslip);
    setComponents(null);
    try {
      const { data } = await apiClient.get(`/payroll/payslips/employees/${employeeId}/${payslip.id}`);
      setComponents(data.data.components || []);
    } catch (err) {
      showError(getErrorMessage(err, 'Could not load payslip detail'));
    }
  };

  const handleMarkAsPaid = async () => {
    if (!selected) return;
    setMarking(true);
    try {
      await apiClient.post(
        `/payroll/runs/${selected.summary.payrollRunId}/employees/${selected.summary.id}/mark-paid`
      );
      success('✅ Marked as paid');
      const updated = { ...selected, summary: { ...selected.summary, paymentStatus: 'paid' } };
      setSelected(updated);
      setPayslips((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
    } catch (err) {
      showError(getErrorMessage(err, 'Could not mark as paid'));
    } finally {
      setMarking(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleShareWhatsApp = () => {
    if (!selected) return;
    const name = employee ? `${employee.firstName} ${employee.lastName}` : 'Employee';
    const issued = selected.issuedAt ? new Date(selected.issuedAt).toLocaleDateString() : '';
    const lines = [
      `Payslip — ${name}`,
      issued ? `Issued: ${issued}` : null,
      `Gross Pay: £${Number(selected.summary?.grossPay ?? 0).toFixed(2)}`,
      `Deductions: £${Number(selected.summary?.totalDeductions ?? 0).toFixed(2)}`,
      `Net Pay: £${Number(selected.summary?.netPay ?? 0).toFixed(2)}`,
      taxRegime?.isEstimate ? `⚠️ ${taxRegime.taxRegimeLabel}` : null,
    ].filter(Boolean);
    const text = encodeURIComponent(lines.join('\n'));
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  return (
    <motion.div className="dashboard-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
      <style>{printStyles}</style>

      <div className="page-header no-print">
        <Link to="/dashboard/employees" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: '#6b7280', textDecoration: 'none', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
          <FiArrowLeft size={14} /> Back to Employees
        </Link>
        <h1>Payslips</h1>
        <p>Every payslip generated for this employee</p>
      </div>

      {taxRegime?.isEstimate && (
        <div className="no-print" style={{
          display: 'flex', alignItems: 'flex-start', gap: '0.75rem', padding: '1rem',
          background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', marginBottom: '1.5rem',
        }}>
          <FiAlertTriangle size={20} style={{ color: '#d97706', flexShrink: 0, marginTop: '0.1rem' }} />
          <div>
            <div style={{ fontWeight: 700, color: '#92400e' }}>Estimate only — not real tax compliance</div>
            <div style={{ fontSize: '0.9rem', color: '#92400e' }}>{taxRegime.taxRegimeLabel}</div>
          </div>
        </div>
      )}

      <section className="page-section no-print">
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Issued</th>
                <th>Gross Pay</th>
                <th>Deductions</th>
                <th>Net Pay</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: '2rem' }}>Loading...</td></tr>
              ) : payslips.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                  <FiFileText size={24} style={{ marginBottom: '0.5rem' }} /><br />No payslips yet — lock a payroll run to generate one
                </td></tr>
              ) : payslips.map((p) => (
                <tr key={p.id}>
                  <td>{p.issuedAt ? new Date(p.issuedAt).toLocaleDateString() : '—'}</td>
                  <td>£{Number(p.summary?.grossPay ?? 0).toFixed(2)}</td>
                  <td>£{Number(p.summary?.totalDeductions ?? 0).toFixed(2)}</td>
                  <td style={{ fontWeight: 600 }}>£{Number(p.summary?.netPay ?? 0).toFixed(2)}</td>
                  <td style={{ textTransform: 'capitalize' }}>
                    <span className="badge" style={{ background: p.summary?.paymentStatus === 'paid' ? '#10b981' : '#f59e0b', color: 'white' }}>
                      {p.summary?.paymentStatus || '—'}
                    </span>
                  </td>
                  <td>
                    <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => viewDetail(p)}>
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {selected && (
        <section className="page-section payslip-print-area">
          <div className="section-header no-print">
            <h2>Payslip Detail — {selected.issuedAt ? new Date(selected.issuedAt).toLocaleDateString() : ''}</h2>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {selected.summary?.paymentStatus !== 'paid' && (
                <button
                  className="btn btn-sm btn-primary"
                  style={{ width: 'auto' }}
                  disabled={marking}
                  onClick={handleMarkAsPaid}
                >
                  <FiCheck size={14} /> {marking ? 'Marking...' : 'Mark as Paid'}
                </button>
              )}
              <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={handlePrint} title="Print">
                <FiPrinter size={14} /> Print
              </button>
              <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={handleShareWhatsApp} title="Share via WhatsApp">
                <FiShare2 size={14} /> WhatsApp
              </button>
              <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => setSelected(null)}>Close</button>
            </div>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <h2 style={{ margin: 0 }}>Payslip — {employee ? `${employee.firstName} ${employee.lastName}` : ''}</h2>
            <p style={{ color: '#6b7280', margin: '0.25rem 0 0 0' }}>
              Issued {selected.issuedAt ? new Date(selected.issuedAt).toLocaleDateString() : '—'}
            </p>
          </div>

          <div className="stats-row">
            <div className="stat-box"><div className="label">Gross Pay</div><div className="value">£{Number(selected.summary?.grossPay ?? 0).toFixed(2)}</div></div>
            <div className="stat-box"><div className="label">Deductions</div><div className="value">£{Number(selected.summary?.totalDeductions ?? 0).toFixed(2)}</div></div>
            <div className="stat-box"><div className="label">Net Pay</div><div className="value">£{Number(selected.summary?.netPay ?? 0).toFixed(2)}</div></div>
            <div className="stat-box"><div className="label">Employer Cost</div><div className="value">£{Number(selected.summary?.employerCostTotal ?? 0).toFixed(2)}</div></div>
          </div>
          {components === null ? (
            <p style={{ color: '#6b7280' }}>Loading breakdown...</p>
          ) : components.length === 0 ? (
            <p style={{ color: '#6b7280' }}>No itemized components recorded.</p>
          ) : (
            <div className="table-container">
              <table className="data-table">
                <thead><tr><th>Component</th><th>Quantity</th><th>Rate</th><th>Amount</th></tr></thead>
                <tbody>
                  {components.map((c) => (
                    <tr key={c.id}>
                      <td>{componentTypeNames[c.payrollComponentTypeId] || `Type #${c.payrollComponentTypeId}`}</td>
                      <td>{c.quantity ? Number(c.quantity).toFixed(2) : '—'}</td>
                      <td>{c.rate ? `£${Number(c.rate).toFixed(2)}` : '—'}</td>
                      <td style={{ fontWeight: 600 }}>£{Number(c.amount).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </motion.div>
  );
};

export default Payslips;